import { useState, useEffect, useRef, useCallback } from 'react';
import { upsertDraft, lookupDraft, deleteDraft } from '@/lib/api';
import { useDraftStore } from '@/stores/draft.store';
import { DRAFT_CLOUD_SAVE_DEBOUNCE_MS } from '@goodchat/shared/constants';

interface UseCloudDraftOptions {
  areaId: string;
  draftType: 'message' | 'thread' | 'announcement';
  threadId?: string;
  debounceMs?: number;
}

export function useCloudDraft<T extends object>(
  defaultValue: T,
  options: UseCloudDraftOptions,
) {
  const { areaId, draftType, threadId, debounceMs = DRAFT_CLOUD_SAVE_DEBOUNCE_MS } = options;

  const [draft, setDraftState] = useState<T>(defaultValue);
  const [loading, setLoading] = useState(true);
  const [hasDraft, setHasDraft] = useState(false);
  const draftIdRef = useRef<string | null>(null);
  const lastSavedRef = useRef<string>('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  // Track whether content is "empty" (all string values are blank)
  const isEmptyDraft = useCallback(
    (value: T): boolean => {
      const obj = value as Record<string, unknown>;
      const def = defaultValue as Record<string, unknown>;
      return Object.entries(obj).every(([key, v]) => {
        if (typeof v === 'string') return v.trim() === '';
        if (Array.isArray(v)) return v.length === 0;
        if (typeof v === 'boolean') return v === def[key];
        return false;
      });
    },
    [defaultValue],
  );

  // Load draft from server on mount
  useEffect(() => {
    mountedRef.current = true;

    if (!areaId) {
      setLoading(false);
      return;
    }

    lookupDraft(areaId, draftType, threadId)
      .then((res) => {
        if (!mountedRef.current) return;
        if (res.draft) {
          const content = res.draft.content as T;
          setDraftState(content);
          setHasDraft(true);
          draftIdRef.current = res.draft.id;
          lastSavedRef.current = JSON.stringify(content);
        }
      })
      .catch(() => {
        // ignore — use default
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });

    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [areaId, draftType, threadId]);

  // Debounced save to server on draft change
  useEffect(() => {
    if (loading || !areaId) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const serialized = JSON.stringify(draft);
      if (serialized === lastSavedRef.current) return;

      // If everything is empty, delete the draft instead
      if (isEmptyDraft(draft)) {
        if (draftIdRef.current) {
          deleteDraft(draftIdRef.current).catch(() => {});
          useDraftStore.getState().decrement();
          useDraftStore.getState().removeAreaId(areaId);
          draftIdRef.current = null;
          lastSavedRef.current = '';
          setHasDraft(false);
        }
        return;
      }

      upsertDraft({
        areaId,
        threadId: threadId ?? null,
        draftType,
        content: draft as Record<string, unknown>,
      })
        .then((res) => {
          if (!mountedRef.current) return;
          const newId = (res.draft as { id?: string })?.id;
          if (!draftIdRef.current && newId) {
            // New draft created
            draftIdRef.current = newId;
            useDraftStore.getState().increment();
            useDraftStore.getState().addAreaId(areaId);
          }
          lastSavedRef.current = serialized;
        })
        .catch(() => {
          // ignore — will retry on next change
        });
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [draft, loading, areaId, draftType, threadId, debounceMs, isEmptyDraft]);

  const setDraft = useCallback((value: T | ((prev: T) => T)) => {
    setDraftState(value);
  }, []);

  const clearDraft = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (draftIdRef.current) {
      deleteDraft(draftIdRef.current).catch(() => {});
      useDraftStore.getState().decrement();
      useDraftStore.getState().removeAreaId(areaId);
    }
    draftIdRef.current = null;
    lastSavedRef.current = '';
    setDraftState(defaultValue);
    setHasDraft(false);
  }, [areaId, defaultValue]);

  return { draft, setDraft, clearDraft, hasDraft, loading };
}
